'use client';

/**
 * TimelineView — SVG message timeline extracted from rfi-widget.tsx
 *
 * Shows message dots on a horizontal time axis, grouped by RFQ thread.
 * If selectedRfqSid is set, shows only that RFQ's chat messages;
 * otherwise shows all project threads.
 *
 * Features: responsive width (ResizeObserver), dot declutter (vertical jitter),
 * click-to-select detail strip, "Today" marker, summary bar.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { Clock, Loader2, XCircle, Paperclip } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

// ── Types ──────────────────────────────────────────────────────

interface ThreadMessage {
  cdk: string;
  body: string;
  author_name: string;
  author_id: string | null;
  platform: string;
  message_type: string;
  created_date: string;
  has_attachments: boolean | null;
  attachment_count: number | null;
  attachment_urls: string[] | null;
  attachment_metadata: string | null;
}

interface ThreadRecord {
  rfq_sid: string;
  rfq_name: string;
  rfq_state: string;
  message_count: number;
  messages: ThreadMessage[];
}

interface ThreadsResponse {
  threads: ThreadRecord[];
  count: number;
}

interface ChatResponse {
  rfq_name: string | null;
  messages: ThreadMessage[];
  message_count: number;
}

/** Positioned dot after jitter calculation */
interface PlottedDot {
  msg: ThreadMessage;
  mi: number;
  cx: number;
  cy: number;
}

// ── Helpers ────────────────────────────────────────────────────

function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '  • ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** Platform → SVG fill colour (HSL tokens from design system) */
const PLATFORM_COLORS: Record<string, string> = {
  whatsapp: 'hsl(var(--success))',
  chatter: 'hsl(var(--warning))',
  klyde_ai: 'hsl(var(--primary))',
  email: 'hsl(var(--muted-foreground))',
  email_outgoing: 'hsl(var(--info, 210 80% 60%))',
  ipe: 'hsl(var(--primary))',
};
const DEFAULT_DOT_COLOR = 'hsl(var(--muted-foreground))';

/** Platform display names */
const PLATFORM_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  chatter: 'Chatter',
  klyde_ai: 'Klyde AI',
  email: 'Email',
  email_outgoing: 'Email Out',
  ipe: 'IPE',
};

// ── Layout constants ─────────────────────────────────────────

const LEFT_LABEL = 140;
const RIGHT_PAD = 24;
const ROW_HEIGHT = 56;
const TOP_PAD = 36;
const DOT_R = 8;
const MIN_GAP_PX = DOT_R * 2 + 2; // dots closer than this get jittered

/** Compute vertical jitter offsets for clustered dots in a row */
function computeJitteredDots(
  messages: ThreadMessage[],
  baseY: number,
  timeToX: (t: number) => number,
): PlottedDot[] {
  const sorted = messages
    .map((msg, mi) => ({ msg, mi, t: new Date(msg.created_date).getTime() }))
    .filter((d) => !isNaN(d.t))
    .sort((a, b) => a.t - b.t);

  const maxJitter = ROW_HEIGHT / 2 - DOT_R - 2;
  const results: PlottedDot[] = [];
  let clusterStart = 0;

  for (let i = 0; i <= sorted.length; i++) {
    const atEnd = i === sorted.length;
    const startX = timeToX(sorted[clusterStart]?.t ?? 0);
    const curX = atEnd ? Infinity : timeToX(sorted[i].t);

    if (atEnd || curX - startX > MIN_GAP_PX) {
      // Flush the cluster [clusterStart..i-1]
      const clusterSize = i - clusterStart;
      for (let j = 0; j < clusterSize; j++) {
        const d = sorted[clusterStart + j];
        // Alternating jitter: 0, -step, +step, -2*step, +2*step...
        let yOffset = 0;
        if (clusterSize > 1) {
          const step = Math.min(12, maxJitter / Math.ceil(clusterSize / 2));
          const rank = j; // 0-based within cluster
          if (rank === 0) yOffset = 0;
          else if (rank % 2 === 1) yOffset = -Math.ceil(rank / 2) * step;
          else yOffset = (rank / 2) * step;
          yOffset = Math.max(-maxJitter, Math.min(maxJitter, yOffset));
        }
        results.push({ msg: d.msg, mi: d.mi, cx: timeToX(d.t), cy: baseY + yOffset });
      }
      clusterStart = i;
    }
  }

  return results;
}

// ── Component ──────────────────────────────────────────────────

export function TimelineView({ projectSid, selectedRfqSid }: { projectSid: string; selectedRfqSid?: string }) {
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);

  // Data state
  const [threads, setThreads] = useState<ThreadRecord[]>([]);
  const [chatMessages, setChatMessages] = useState<ThreadMessage[]>([]);
  const [chatRfqName, setChatRfqName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Responsive width
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(960);

  // Hover state (SVG label only, no overlay)
  const [hoveredDot, setHoveredDot] = useState<PlottedDot | null>(null);

  // Selected message for detail strip
  const [selectedMsg, setSelectedMsg] = useState<{ msg: ThreadMessage; rowLabel: string } | null>(null);

  // ResizeObserver for responsive SVG
  useEffect(() => {
    if (!containerRef.current) return;
    const update = () => {
      const w = containerRef.current!.clientWidth;
      if (w > 0) setContainerWidth(w);
    };
    update();
    const obs = new ResizeObserver(() => update());
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Data fetching
  useEffect(() => {
    setIsLoading(true);
    setError(null);

    if (selectedRfqSid) {
      api
        .getStrategyData<ChatResponse>('rfi_chat', { rfq_sid: selectedRfqSid })
        .then((res) => {
          if (!res.success) { setError(res.error || 'Failed to load'); return; }
          setChatMessages(res.data.messages ?? []);
          setChatRfqName(res.data.rfq_name ?? null);
          setThreads([]);
        })
        .catch((err) => setError(err.message ?? 'Network error'))
        .finally(() => setIsLoading(false));
    } else {
      api
        .getStrategyData<ThreadsResponse>('rfi_threads', { project_sid: projectSid })
        .then((res) => {
          if (!res.success) { setError(res.error || 'Failed to load'); return; }
          setThreads(res.data.threads ?? []);
          setChatMessages([]);
          setChatRfqName(null);
        })
        .catch((err) => setError(err.message ?? 'Network error'))
        .finally(() => setIsLoading(false));
    }
  }, [projectSid, selectedRfqSid]);

  // Build rows
  const rows: { label: string; messages: ThreadMessage[] }[] = useMemo(() => {
    if (selectedRfqSid) return [{ label: chatRfqName ?? 'RFQ', messages: chatMessages }];
    return threads
      .filter((t) => t.message_count > 0)
      .map((t) => ({ label: t.rfq_name, messages: t.messages }));
  }, [selectedRfqSid, chatRfqName, chatMessages, threads]);

  // Summary stats
  const allMessages = useMemo(() => rows.flatMap((r) => r.messages), [rows]);
  const platformCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allMessages.forEach((m) => { counts[m.platform] = (counts[m.platform] || 0) + 1; });
    return counts;
  }, [allMessages]);
  const attachmentCount = useMemo(
    () => allMessages.filter((m) => m.has_attachments || (m.attachment_count && m.attachment_count > 0)).length,
    [allMessages],
  );

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-10 h-10 mx-auto mb-3 text-destructive" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (rows.length === 0 || rows.every((r) => r.messages.length === 0)) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No messages to show on timeline</p>
        </div>
      </div>
    );
  }

  // Time bounds
  const allDates = allMessages
    .map((m) => new Date(m.created_date).getTime())
    .filter((t) => !isNaN(t));

  const minTime = Math.min(...allDates);
  const maxTime = Math.max(...allDates);
  const span = maxTime - minTime || 1;
  const padded = { min: minTime - span * 0.05, max: maxTime + span * 0.05 };
  const totalSpan = padded.max - padded.min;

  // Responsive SVG width (subtract padding: p-4 = 16px each side)
  const svgWidth = Math.max(containerWidth - 32, 400);
  const trackWidth = svgWidth - LEFT_LABEL - RIGHT_PAD;
  const svgHeight = TOP_PAD + rows.length * ROW_HEIGHT + 16;

  const timeToX = (t: number) => LEFT_LABEL + ((t - padded.min) / totalSpan) * trackWidth;

  // Time axis ticks
  const tickCount = Math.min(6, Math.max(2, Math.ceil(span / (24 * 3600 * 1000))));
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => {
    const t = minTime + (span * i) / tickCount;
    return { x: timeToX(t), label: new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) };
  });

  // Today marker
  const todayMs = Date.now();
  const showToday = todayMs >= padded.min && todayMs <= padded.max;
  const todayX = timeToX(todayMs);

  // Click handler (fires selectNode + sets detail strip)
  const handleDotClick = (msg: ThreadMessage, mi: number, rowLabel: string) => {
    selectNode({
      id: msg.cdk || `msg-${mi}`,
      label: msg.author_name || 'Message',
      type: 'message',
      status: msg.platform ?? 'unknown',
      properties: {
        body: msg.body ? stripHtml(msg.body) : '',
        author_name: msg.author_name,
        platform: msg.platform,
        message_type: msg.message_type,
        created_date: msg.created_date,
        has_attachments: msg.has_attachments,
        attachment_count: msg.attachment_count,
        attachment_urls: msg.attachment_urls,
        cdk: msg.cdk,
      },
    });
    setSelectedMsg({ msg, rowLabel });
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="shrink-0 border-b border-border/50 bg-background/95 backdrop-blur-sm px-6 py-3">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-primary" />
          <span className="font-semibold">Timeline</span>
          {/* Legend */}
          <div className="flex items-center gap-3 ml-auto text-[10px]">
            {Object.entries(PLATFORM_COLORS).map(([key, color]) => (
              <span key={key} className="flex items-center gap-1">
                <svg width="10" height="10"><circle cx="5" cy="5" r="4" fill={color} /></svg>
                {PLATFORM_LABELS[key] ?? key.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* SVG Timeline — scrollable area */}
      <div ref={containerRef} className="flex-1 overflow-auto scrollbar-thin p-4 min-h-0">
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="w-full"
          style={{ minHeight: svgHeight, maxHeight: Math.max(svgHeight, 300) }}
          onMouseLeave={() => setHoveredDot(null)}
        >
          {/* Time axis ticks */}
          {ticks.map((tick, i) => (
            <g key={i}>
              <line
                x1={tick.x} y1={TOP_PAD - 8}
                x2={tick.x} y2={svgHeight - 8}
                stroke="hsl(var(--border))" strokeWidth={0.5} strokeDasharray="4 4"
              />
              <text
                x={tick.x} y={TOP_PAD - 14}
                textAnchor="middle"
                className="fill-muted-foreground"
                fontSize={10}
              >
                {tick.label}
              </text>
            </g>
          ))}

          {/* Today marker */}
          {showToday && (
            <g>
              <line
                x1={todayX} y1={TOP_PAD - 4}
                x2={todayX} y2={svgHeight - 8}
                stroke="hsl(var(--primary))" strokeWidth={1.5} strokeDasharray="6 3" opacity={0.6}
              />
              <rect
                x={todayX - 20} y={TOP_PAD - 20}
                width={40} height={14} rx={7}
                fill="hsl(var(--primary))"
              />
              <text
                x={todayX} y={TOP_PAD - 10}
                textAnchor="middle" fontSize={9} fontWeight={600}
                fill="hsl(var(--primary-foreground))"
              >
                Today
              </text>
            </g>
          )}

          {/* Rows */}
          {rows.map((row, ri) => {
            const baseY = TOP_PAD + ri * ROW_HEIGHT + ROW_HEIGHT / 2;
            const dots = computeJitteredDots(row.messages, baseY, timeToX);

            return (
              <g key={ri}>
                {/* Row label */}
                <text
                  x={LEFT_LABEL - 8}
                  y={baseY + 4}
                  textAnchor="end"
                  className="fill-foreground"
                  fontSize={12}
                  fontWeight={600}
                >
                  {row.label.length > 18 ? row.label.slice(0, 18) + '\u2026' : row.label}
                </text>

                {/* Track line */}
                <line
                  x1={LEFT_LABEL} y1={baseY}
                  x2={svgWidth - RIGHT_PAD} y2={baseY}
                  stroke="hsl(var(--border))" strokeWidth={1}
                />

                {/* Message dots (jittered) */}
                {dots.map((dot) => {
                  const color = PLATFORM_COLORS[dot.msg.platform] ?? DEFAULT_DOT_COLOR;
                  const dotId = dot.msg.cdk || `msg-${dot.mi}`;
                  const isSelected = selectedNode?.id === dotId;
                  const isHovered = hoveredDot?.msg.cdk === dot.msg.cdk && hoveredDot?.mi === dot.mi;
                  const r = isHovered ? DOT_R + 2 : isSelected ? DOT_R + 1 : DOT_R;

                  return (
                    <g
                      key={dotId}
                      onClick={() => handleDotClick(dot.msg, dot.mi, row.label)}
                      onMouseEnter={() => setHoveredDot(dot)}
                      onMouseLeave={() => setHoveredDot(null)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* Selected ring */}
                      {isSelected && (
                        <circle
                          cx={dot.cx} cy={dot.cy} r={DOT_R + 5}
                          fill="transparent"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          opacity={0.8}
                        />
                      )}
                      {/* Dot */}
                      <circle
                        cx={dot.cx} cy={dot.cy} r={r}
                        fill={color}
                        opacity={isHovered || isSelected ? 1 : 0.85}
                        className="transition-all duration-150"
                      />
                      {/* Invisible hit area */}
                      <circle
                        cx={dot.cx} cy={dot.cy} r={DOT_R + 6}
                        fill="transparent"
                      />
                    </g>
                  );
                })}

                {/* Hover label — positioned above the hovered dot, SVG text only */}
                {hoveredDot && dots.some((d) => d.msg.cdk === hoveredDot.msg.cdk && d.mi === hoveredDot.mi) && (() => {
                  const name = hoveredDot.msg.author_name || hoveredDot.msg.platform || '?';
                  const truncated = name.length > 16 ? name.slice(0, 15) + '\u2026' : name;
                  return (
                    <text
                      x={hoveredDot.cx}
                      y={hoveredDot.cy - DOT_R - 6}
                      textAnchor="middle"
                      className="fill-foreground pointer-events-none"
                      fontSize={10}
                      fontWeight={500}
                    >
                      {truncated}
                    </text>
                  );
                })()}
              </g>
            );
          })}
        </svg>
      </div>

      {/* Detail strip — shows selected message info, never overlays dots */}
      {selectedMsg && (
        <div className="shrink-0 border-t border-border bg-muted/50 px-6 py-2.5">
          <div className="flex items-start gap-3">
            <svg width="14" height="14" className="mt-0.5 shrink-0">
              <circle cx="7" cy="7" r="5" fill={PLATFORM_COLORS[selectedMsg.msg.platform] ?? DEFAULT_DOT_COLOR} />
            </svg>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-semibold text-foreground">
                  {selectedMsg.msg.author_name || 'Unknown'}
                </span>
                <span className="text-muted-foreground">
                  {PLATFORM_LABELS[selectedMsg.msg.platform] ?? selectedMsg.msg.platform}
                </span>
                <span className="text-muted-foreground">
                  {new Date(selectedMsg.msg.created_date).toLocaleString(undefined, {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                {selectedMsg.msg.has_attachments && (
                  <span className="flex items-center gap-0.5 text-muted-foreground">
                    <Paperclip className="w-3 h-3" />
                    {selectedMsg.msg.attachment_count ?? 1}
                  </span>
                )}
                <span className="text-muted-foreground/50 ml-auto">{selectedMsg.rowLabel}</span>
              </div>
              {selectedMsg.msg.body && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate">
                  {stripHtml(selectedMsg.msg.body).slice(0, 200)}
                </p>
              )}
            </div>
            <button
              onClick={() => setSelectedMsg(null)}
              className="text-muted-foreground hover:text-foreground text-xs ml-2 shrink-0"
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Summary bar */}
      <div className="shrink-0 flex items-center gap-4 px-6 py-2 border-t border-border bg-muted/30 text-[11px] text-muted-foreground">
        <span className="font-medium">{allMessages.length} messages</span>
        <span className="text-border">|</span>
        {Object.entries(platformCounts)
          .sort(([, a], [, b]) => b - a)
          .map(([platform, count]) => (
            <span key={platform} className="flex items-center gap-1">
              <svg width="8" height="8"><circle cx="4" cy="4" r="3" fill={PLATFORM_COLORS[platform] ?? DEFAULT_DOT_COLOR} /></svg>
              {count} {PLATFORM_LABELS[platform] ?? platform}
            </span>
          ))}
        {attachmentCount > 0 && (
          <>
            <span className="text-border">|</span>
            <span className="flex items-center gap-1">
              <Paperclip className="w-3 h-3" />
              {attachmentCount} with attachments
            </span>
          </>
        )}
      </div>
    </div>
  );
}
